import { Vector3 } from 'three';
import { TrackRuntime } from '../track/runtime';
import { Frame } from '../track/frames';
import { sweepProfile, SweepMesh } from '../geometry/sweep';
import {
  buildGearRackMeshes,
  normalizeTrackShapeSettings,
  printTrackProfile,
  trackLoopsForShape,
  TrackShapeSettings,
} from '../geometry/gearRack';
import { TrackSupportPrimitive } from '../supports/types';
import { solidJunctionMesh } from './joints';
import {
  ensureBeamJointHubs,
  prepareSupportPrimitives,
  flatFooterMesh,
  cylinderMesh,
  HUB_MATCH_M,
  endOnFooterPad,
} from './supportPrims';

const PRINT_SAMPLE_STEP = 0.28;

export interface HubSpec {
  id: string;
  pos: Vector3;
  radius: number;
}

export interface MemberSpec {
  id: string;
  kind: 'column' | 'brace' | 'knee' | 'stub';
  a: Vector3;
  b: Vector3;
  radiusA: number;
  radiusB: number;
  /** Hub ids at endpoints when within match distance. */
  hubAtA?: string;
  hubAtB?: string;
}

export interface WholeMeshSources {
  trackMeshes: SweepMesh[];
  supportMeshes: SweepMesh[];
  hubs: HubSpec[];
  members: MemberSpec[];
  /** All meshes that form the single coaster solid. */
  allMeshes: SweepMesh[];
}

/**
 * Mesh the entire coaster once — continuous track + full support members + hubs.
 * No chunk caps, no mid-member pre-splits. Cutting happens later on the solid.
 */
export function buildWholeMeshSources(
  runtime: TrackRuntime,
  supportPrimitives: TrackSupportPrimitive[],
  trackShape?: Partial<TrackShapeSettings> | null,
): WholeMeshSources {
  const trackMeshes = buildFullTrackMeshes(runtime, trackShape);
  const prepared = ensureBeamJointHubs(prepareSupportPrimitives(supportPrimitives));
  const { supportMeshes, hubs, members } = buildFullSupportMeshes(prepared);
  return {
    trackMeshes,
    supportMeshes,
    hubs,
    members,
    allMeshes: [...trackMeshes, ...supportMeshes],
  };
}

export function buildFullTrackMeshes(
  runtime: TrackRuntime,
  trackShape?: Partial<TrackShapeSettings> | null,
): SweepMesh[] {
  const L = runtime.totalLength;
  if (L < 0.05) return [];
  const closed = runtime.centerline.track.closed;
  const n = Math.max(2, Math.ceil(L / PRINT_SAMPLE_STEP));
  const frames: Frame[] = [];
  for (let i = 0; i <= n; i++) {
    const s = closed ? (L * i) / n : (L * i) / n;
    frames.push(runtime.frameAt(Math.min(s, L)));
  }
  // Drop duplicate end frame for closed loops (last == first).
  if (closed && frames.length > 2) frames.pop();

  const shape = normalizeTrackShapeSettings(trackShape);
  const profile = printTrackProfile(shape);
  const { rails, spine } = trackLoopsForShape(shape, 'print', 14);
  const pieces: SweepMesh[] = [
    ...rails.map((loop) =>
      sweepProfile(frames, loop, { capEnds: !closed, closeLoop: closed }),
    ),
    ...spine.map((loop) =>
      sweepProfile(frames, loop, { capEnds: !closed, closeLoop: closed }),
    ),
  ];

  pieces.push(...buildGearRackMeshes(runtime, 0, L, shape, profile));
  return pieces;
}

function buildFullSupportMeshes(prims: TrackSupportPrimitive[]): {
  supportMeshes: SweepMesh[];
  hubs: HubSpec[];
  members: MemberSpec[];
} {
  const supportMeshes: SweepMesh[] = [];
  const hubs: HubSpec[] = [];
  const members: MemberSpec[] = [];
  let hubSeq = 0;
  let memSeq = 0;

  const footers = prims.filter((p) => p.kind === 'footer');

  for (const prim of prims) {
    if (prim.kind === 'footer') {
      supportMeshes.push(flatFooterMesh(prim.pos, prim.radius, prim.height));
      continue;
    }
    if (prim.kind === 'joint') {
      const id = `hub-${++hubSeq}`;
      hubs.push({ id, pos: prim.pos.clone(), radius: prim.radius });
      supportMeshes.push(solidJunctionMesh(prim.pos, prim.radius));
      continue;
    }
    const id = `mem-${++memSeq}`;
    const radiusA = prim.kind === 'stub' ? prim.radiusA : prim.radius;
    const radiusB = prim.kind === 'stub' ? prim.radiusB : prim.radius;
    members.push({
      id,
      kind: prim.kind,
      a: prim.a.clone(),
      b: prim.b.clone(),
      radiusA,
      radiusB,
    });
    supportMeshes.push(
      cylinderMesh(prim.a, prim.b, radiusA, radiusB, {
        horizontalCapA: endOnFooterPad(prim.a, footers),
        horizontalCapB: endOnFooterPad(prim.b, footers),
      }),
    );
  }

  for (const m of members) {
    for (const h of hubs) {
      if (m.a.distanceTo(h.pos) < HUB_MATCH_M) m.hubAtA = h.id;
      if (m.b.distanceTo(h.pos) < HUB_MATCH_M) m.hubAtB = h.id;
    }
  }

  return { supportMeshes, hubs, members };
}
